import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { InputField } from "components/designV2/inputfield/inputfield";
import { PhoneInput } from "components/designV2/phoneInput/phoneInput";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import type { SearchableDropdownOption } from "components/designV2/searchableDropdown/searchableDropdown.types";
import { CustomIcon } from "components/icon/customIcon.component";
import type { StaffProfileData } from "services/api/company/staff.service";
import {
  FieldCard,
  FieldLabel,
  FieldsGrid,
  FieldValue,
  Section,
  SectionActionRow,
  SectionBox,
  SectionTitle,
  SingleColumnFieldsGrid,
  StatValueRow,
  TwoColumnFieldsGrid,
} from "web-apps/company/components/staff/components/profilePanel/layout/profilePanel.styled";
import type { PersonalDetailsForm } from "web-apps/company/components/staff/components/profilePanel/profilePanel.contract";
import { getPersonalFieldValidationErrors } from "web-apps/company/components/staff/components/profilePanel/state/profilePanel.state.validation.logic";
import { formatStaffPhoneNumber } from "web-apps/company/components/staff/components/shared/staffPhoneNumber.logic";
import { formatPersonalIdentityNumber } from "web-apps/company/utils/personalIdentityNumber.utils";

interface ProfilePanelProfileSectionProps {
  readonly editedPersonalDetails: PersonalDetailsForm;
  readonly hasStatistics: boolean;
  readonly isEditMode: boolean;
  readonly profile: StaffProfileData;
  readonly ratingCountLabel: string;
  readonly ratingLabel: string;
  readonly teamLabels: string[];
  readonly teamOptions: SearchableDropdownOption[];
  readonly workedHoursLabel: string;
  readonly onOpenChangeTeamModal: () => void;
  readonly onPersonalFieldChange: <TField extends keyof PersonalDetailsForm>(
    field: TField,
    value: PersonalDetailsForm[TField],
  ) => void;
}

function formatPostalCode(postalCode: string | null): string {
  const normalizedValue = (postalCode ?? "").trim();
  if (normalizedValue.length === 0) return "";

  const digits = normalizedValue.replace(/\s/g, "");
  if (/^\d{5}$/.test(digits)) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 5)}`;
  }
  return normalizedValue;
}

export const ProfilePanelProfileSection = (
  props: ProfilePanelProfileSectionProps,
): JSX.Element => {
  const { t } = useTranslation();
  // A number is invalid for most of the time it takes to type one, so the
  // complaint waits until the field is left.
  const [showPhoneNumberError, setShowPhoneNumberError] = useState(false);
  const validationErrors = getPersonalFieldValidationErrors(
    props.editedPersonalDetails,
  );

  return (
    <>
      <Section>
        <SectionTitle>{t("Staff.ProfilePanelPersonalDetails")}</SectionTitle>
        <SectionBox>
          <TwoColumnFieldsGrid>
            <FieldCard>
              {props.isEditMode ? (
                <PhoneInput
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldPhoneNumberMandatory")}
                  value={props.editedPersonalDetails.phoneNumber}
                  hasError={
                    showPhoneNumberError && validationErrors.phoneNumber != null
                  }
                  errorText={
                    validationErrors.phoneNumber == null
                      ? undefined
                      : t(validationErrors.phoneNumber.translationKey)
                  }
                  onChange={(phoneNumber) => {
                    setShowPhoneNumberError(false);
                    props.onPersonalFieldChange("phoneNumber", phoneNumber);
                  }}
                  onBlur={() => setShowPhoneNumberError(true)}
                />
              ) : (
                <>
                  <FieldValue>
                    {formatStaffPhoneNumber(props.profile.phoneNumber)}
                  </FieldValue>
                  <FieldLabel>{t("Staff.PhoneNumber")}</FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("OrganizationalStructure.FirstName")}
                  value={props.editedPersonalDetails.firstName}
                  hasError={validationErrors.firstName != null}
                  errorText={
                    validationErrors.firstName == null
                      ? undefined
                      : t(validationErrors.firstName.translationKey)
                  }
                  onChange={(firstName) =>
                    props.onPersonalFieldChange("firstName", firstName)
                  }
                />
              ) : (
                <>
                  <FieldValue>{props.profile.firstName ?? "-"}</FieldValue>
                  <FieldLabel>
                    {t("OrganizationalStructure.FirstName")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("OrganizationalStructure.LastName")}
                  value={props.editedPersonalDetails.lastName}
                  hasError={validationErrors.lastName != null}
                  errorText={
                    validationErrors.lastName == null
                      ? undefined
                      : t(validationErrors.lastName.translationKey)
                  }
                  onChange={(lastName) =>
                    props.onPersonalFieldChange("lastName", lastName)
                  }
                />
              ) : (
                <>
                  <FieldValue>{props.profile.lastName ?? "-"}</FieldValue>
                  <FieldLabel>
                    {t("OrganizationalStructure.LastName")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldEmail")}
                  type="email"
                  value={props.editedPersonalDetails.email}
                  hasError={validationErrors.email != null}
                  errorText={
                    validationErrors.email == null
                      ? undefined
                      : t(validationErrors.email.translationKey)
                  }
                  onChange={(email) =>
                    props.onPersonalFieldChange("email", email)
                  }
                />
              ) : (
                <>
                  <FieldValue>{props.profile.email ?? "-"}</FieldValue>
                  <FieldLabel>{t("Staff.AddStaffPanelFieldEmail")}</FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldPersonalNumber")}
                  hintText={t("Staff.AddStaffPanelHintPersonalNumberFormat")}
                  value={props.editedPersonalDetails.personalIdentityNumber}
                  hasError={validationErrors.personalIdentityNumber != null}
                  errorText={
                    validationErrors.personalIdentityNumber == null
                      ? undefined
                      : t(
                          validationErrors.personalIdentityNumber
                            .translationKey,
                        )
                  }
                  onChange={(personalIdentityNumber) =>
                    props.onPersonalFieldChange(
                      "personalIdentityNumber",
                      personalIdentityNumber,
                    )
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {formatPersonalIdentityNumber(
                      props.profile.personalIdentityNumber,
                    )}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldPersonalNumber")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("Staff.ProfilePanelAddress")}
                  value={props.profile.address ?? ""}
                  disabled
                />
              ) : (
                <>
                  <FieldValue>{props.profile.address ?? "-"}</FieldValue>
                  <FieldLabel>{t("Staff.ProfilePanelAddress")}</FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("Staff.ProfilePanelPostalCode")}
                  value={formatPostalCode(props.profile.postalCode)}
                  disabled
                />
              ) : (
                <>
                  <FieldValue>
                    {formatPostalCode(props.profile.postalCode) || "-"}
                  </FieldValue>
                  <FieldLabel>{t("Staff.ProfilePanelPostalCode")}</FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("Staff.ProfilePanelCity")}
                  value={props.profile.city ?? ""}
                  disabled
                />
              ) : (
                <>
                  <FieldValue>{props.profile.city ?? "-"}</FieldValue>
                  <FieldLabel>{t("Staff.ProfilePanelCity")}</FieldLabel>
                </>
              )}
            </FieldCard>
          </TwoColumnFieldsGrid>
        </SectionBox>
      </Section>

      {props.profile.description &&
        props.profile.description.trim().length > 0 && (
          <Section>
            <SectionTitle>{t("Staff.ProfilePanelDescription")}</SectionTitle>
            <SectionBox>
              <SingleColumnFieldsGrid>
                <FieldCard>
                  <FieldValue>{props.profile.description}</FieldValue>
                </FieldCard>
              </SingleColumnFieldsGrid>
            </SectionBox>
          </Section>
        )}

      <Section>
        <SectionTitle>Team/s</SectionTitle>
        <SectionBox>
          {props.isEditMode ? (
            <TwoColumnFieldsGrid>
              <FieldCard>
                <SearchableDropdown
                  fullWidth
                  label={`${t("Staff.AddStaffPanelFieldTeam")} (${t("Staff.AddStaffPanelMandatory")})`}
                  options={props.teamOptions}
                  selectedValues={props.editedPersonalDetails.teamIds}
                  multiSelect
                  onSelectedValuesChange={(teamIds) =>
                    props.onPersonalFieldChange("teamIds", teamIds)
                  }
                  placeholder={t("Staff.AddStaffPanelPlaceholderNotAssigned")}
                />
              </FieldCard>
            </TwoColumnFieldsGrid>
          ) : (
            <>
              <TwoColumnFieldsGrid>
                <FieldCard>
                  <FieldValue>
                    {props.teamLabels.length > 0
                      ? props.teamLabels.join(", ")
                      : "-"}
                  </FieldValue>
                </FieldCard>
              </TwoColumnFieldsGrid>
              <SectionActionRow>
                <SecondaryButton
                  label={t("Staff.ProfilePanelChangeTeam")}
                  size="small"
                  onClick={props.onOpenChangeTeamModal}
                />
              </SectionActionRow>
            </>
          )}
        </SectionBox>
      </Section>

      <Section>
        <SectionTitle>{t("Staff.ProfilePanelStatistics")}</SectionTitle>
        <SectionBox>
          {props.hasStatistics ? (
            <FieldsGrid>
              <FieldCard>
                <StatValueRow>
                  <CustomIcon name="star-filled" size="16px" />
                  <FieldValue>{props.ratingLabel}</FieldValue>
                </StatValueRow>
                <FieldLabel>{t("Staff.ProfilePanelStatsRating")}</FieldLabel>
              </FieldCard>
              <FieldCard>
                <FieldValue>{props.ratingCountLabel}</FieldValue>
                <FieldLabel>{t("Staff.ProfilePanelStatsRatings")}</FieldLabel>
              </FieldCard>
              <FieldCard>
                <FieldValue>{props.workedHoursLabel}</FieldValue>
                <FieldLabel>
                  {t("OrganizationalStructure.WorkedHours")}
                </FieldLabel>
              </FieldCard>
            </FieldsGrid>
          ) : (
            <FieldValue>{t("Staff.ProfilePanelNoStatistics")}</FieldValue>
          )}
        </SectionBox>
      </Section>
    </>
  );
};
